import { Box, Button, Checkbox, FormControl, FormLabel, Input, Menu, MenuButton, MenuItem, MenuList, Tag, TagCloseButton, TagLabel, Text, } from '@chakra-ui/react';
import { useMemo, useState } from 'react';

type MultiSelectMenuProps<T> = {
  label?: string;
  options: T[];
  selectedValues: number[];
  setSelectedValues: (val: number[]) => void;
  labelKey: keyof T;
  valueKey: keyof T;
  isRequired?: boolean;
  placeholder?: string;
  colorScheme?: string;
  overflow?: boolean
  ShowNumberOfTiles?: number;
  isDisabled?: boolean;
  hideSelectedValues?: boolean;
  mb?: number;
};

export function MultiSelectMenu<T extends Record<string, any>>({
  label,
  options = [],
  selectedValues,
  setSelectedValues,
  labelKey,
  valueKey,
  placeholder = '',
  isRequired = false,
  colorScheme = 'blue',
  overflow = false,
  ShowNumberOfTiles,
  isDisabled = false,
  hideSelectedValues = false,
  mb = 4,
}: MultiSelectMenuProps<T>) {
  const [search, setSearch] = useState('');
  const safeOptions = Array.isArray(options) ? options : []; // ✅ ensure always array

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return safeOptions;

    return safeOptions.filter((item) =>
      String(item[labelKey])
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [search, safeOptions, labelKey]);


  const handleSelectionChange = (id: number) => {
    let newSelected: number[] = [];

    if (id === 0) {
      if (!selectedValues?.includes(0)) {
        newSelected = safeOptions.map((opt) => opt[valueKey] as number);
      } else {
        newSelected = [];
      }
    } else {
      if (selectedValues?.includes(id)) {
        newSelected = selectedValues.filter((val) => val !== id);
      } else {
        newSelected = [...selectedValues, id];
      }

      // remove "0" if not all selected
      if (newSelected.includes(0)) {
        const allIds = safeOptions.map((opt) => opt[valueKey] as number);
        if (newSelected.length !== allIds.length) {
          newSelected = newSelected.filter((val) => val !== 0);
        }
      }
    }

    setSelectedValues(newSelected);
  };


  return (
    <FormControl mb={mb} isRequired={isRequired} >
      <FormLabel>{label}</FormLabel>
      <Menu closeOnSelect={false} >
        <MenuButton as={Button} isDisabled={isDisabled} variant="outline" w="100%" textAlign="left">
          {selectedValues?.length === 0 ? (
            <Text color="gray.400">{placeholder}</Text>
          ) : (
            `${selectedValues?.length} selected`
          )}
        </MenuButton>

        <MenuList maxH="200px" overflowY="auto" p={2}>


          <Box px={2} pb={2} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <Input size="sm" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </Box>

          {/* Options */}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((item) => {
              const id = item[valueKey] as number;
              const name = item[labelKey];
              return (
                <MenuItem key={id} closeOnSelect={false}>
                  <Checkbox
                    isChecked={selectedValues?.includes(id)}
                    onChange={() => handleSelectionChange(id)}
                  >
                    {name}
                  </Checkbox>
                </MenuItem>
              );
            })
          ) : (
            <Text px={3} py={2} fontSize="sm" color="gray.500">
              No results found
            </Text>
          )}
        </MenuList>
      </Menu>

      {!hideSelectedValues && selectedValues?.length > 0 && (
        <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
          {selectedValues.slice(0, overflow ? ShowNumberOfTiles : selectedValues.length).map((id) => {
            const labelItem = safeOptions.find((item) => item[valueKey] === id);
            const name = labelItem?.[labelKey] ?? id;
            return (
              <Tag key={id} size="md" variant="subtle" colorScheme={colorScheme}>
                <TagLabel>{name}</TagLabel>
                <TagCloseButton onClick={() => handleSelectionChange(id)} />
              </Tag>
            );
          })}
          {ShowNumberOfTiles && selectedValues.length > ShowNumberOfTiles && overflow && <p>....</p>}
        </Box>
      )}
    </FormControl>
  );
}
